import React, { useState, useRef, useEffect } from 'react';
import { AudioFile } from '../types';

interface AudioEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (audio: AudioFile) => void;
}

const AudioEditorModal: React.FC<AudioEditorModalProps> = ({ isOpen, onClose, onSave }) => {
  const [audioName, setAudioName] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [startTime, setStartTime] = useState<number>(0);
  const [endTime, setEndTime] = useState<number>(0);
  const [audioDuration, setAudioDuration] = useState<number>(0);
  const [isYoutubeMode, setIsYoutubeMode] = useState<boolean>(false);
  
  const audioRef = useRef<HTMLAudioElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.addEventListener('loadedmetadata', () => {
        if (audioRef.current) {
          setAudioDuration(audioRef.current.duration);
          setEndTime(audioRef.current.duration);
        }
      });
    }
  }, [audioUrl]);
  
  const resetForm = () => {
    setAudioName('');
    setSelectedFile(null);
    setAudioUrl('');
    setYoutubeUrl('');
    setIsProcessing(false);
    setError('');
    setStartTime(0);
    setEndTime(0);
    setAudioDuration(0);
    setIsYoutubeMode(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // 檢查檔案類型和副檔名
    const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'video/mp4'];
    const validExtensions = ['.mp3', '.mp4', '.m4a'];
    
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setError('請上傳 MP3, MP4 或 M4A 格式的音訊檔案');
      setSelectedFile(null);
      setAudioUrl('');
      return;
    }
    
    // 不再限制上傳檔案大小，改為在儲存時檢查
    
    setSelectedFile(file);
    setError('');
    setAudioUrl(URL.createObjectURL(file));
    
    // 如果沒有設定名稱，使用檔案名稱（去除副檔名）
    if (!audioName) {
      const fileName = file.name.split('.').slice(0, -1).join('.');
      setAudioName(fileName);
    }
  };
  
  const handleYoutubeUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setYoutubeUrl(e.target.value);
    setError('');
  };
  
  const extractYoutubeAudio = async () => {
    // 這裡只是一個示意，實際上前端無法直接從YouTube提取音訊
    // 需要使用第三方服務或後端API
    setError('由於技術限制，目前無法直接從YouTube提取音訊。請考慮使用其他方法獲取音訊檔案。');
    return false;
  };
  
  const handleModeToggle = () => {
    setIsYoutubeMode(!isYoutubeMode);
    setError('');
    setAudioUrl('');
    setSelectedFile(null);
    setYoutubeUrl('');
  };
  
  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartTime = parseFloat(e.target.value);
    setStartTime(newStartTime);
    if (audioRef.current && newStartTime < endTime) {
      audioRef.current.currentTime = newStartTime;
    }
  };
  
  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndTime(parseFloat(e.target.value));
  };
  
  const handlePlaySegment = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = startTime;
      audioRef.current.play();
      
      // 播放到結束時間後停止
      const checkTime = () => {
        if (audioRef.current && audioRef.current.currentTime >= endTime) {
          audioRef.current.pause();
          audioRef.current.removeEventListener('timeupdate', checkTime);
        }
      };
      
      audioRef.current.addEventListener('timeupdate', checkTime);
    }
  };
  
  // 下載剪輯後的音訊
  const downloadTrimmedAudio = async () => {
    if (!audioName.trim()) {
      setError('請輸入音訊名稱');
      return;
    }
    
    if (!audioUrl) {
      setError('請上傳音訊檔案');
      return;
    }
    
    if (startTime >= endTime) {
      setError('開始時間必須小於結束時間');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      // 裁剪音訊
      const audioBlob = await trimAudio();
      if (!audioBlob) {
        throw new Error('音訊處理失敗');
      }
      
      // 創建下載連結
      const url = URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${audioName.trim()}.webm`; // 使用webm擴展名
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error processing audio for download:', err);
      setError('處理音訊時發生錯誤');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSave = async () => {
    if (!audioName.trim()) {
      setError('請輸入音訊名稱');
      return;
    }
    
    if (!audioUrl) {
      setError('請上傳音訊檔案');
      return;
    }
    
    if (startTime >= endTime) {
      setError('開始時間必須小於結束時間');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      // 裁剪音訊
      const audioBlob = await trimAudio();
      if (!audioBlob) {
        throw new Error('音訊處理失敗');
      }
      
      // 檢查剪輯後的檔案大小
      if (audioBlob.size > 10 * 1024 * 1024) {
        setError('剪輯後的音訊檔案超過 10MB，請選擇較短的片段或較低品質的音訊');
        setIsProcessing(false);
        return;
      }
      
      // 轉換為 Base64
      const base64Data = await blobToBase64(audioBlob);
      
      // 創建自定義音訊物件
      const customAudio: AudioFile = {
        name: audioName.trim(),
        path: `custom-${Date.now()}`, // 使用時間戳作為唯一路徑
        category: 'custom',
        isCustom: true,
        audioData: base64Data
      };
      
      // 通知父元件
      onSave(customAudio);
      onClose();
    } catch (err) {
      console.error('Error processing audio:', err);
      setError('處理音訊時發生錯誤');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // 裁剪音訊
  const trimAudio = async (): Promise<Blob | null> => {
    return new Promise((resolve) => {
      // 這裡使用簡單的方法：重新播放並錄製指定時間段
      // 實際應用中可能需要使用Web Audio API進行更精確的處理
      
      if (!audioUrl) {
        resolve(null);
        return;
      }
      
      const audio = new Audio(audioUrl);
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const destination = audioContext.createMediaStreamDestination();
      const source = audioContext.createMediaElementSource(audio);
      source.connect(destination);
      source.connect(audioContext.destination);
      
      // 使用更兼容的MIME類型
      const recorder = new MediaRecorder(destination.stream, {
        mimeType: 'audio/webm' // 使用webm格式，兼容性更好
      });
      const chunks: BlobPart[] = [];
      
      recorder.ondataavailable = (e) => {
        chunks.push(e.data);
      };
      
      recorder.onstop = () => {
        // 使用audio/webm格式，大多數瀏覽器都支持
        const blob = new Blob(chunks, { type: 'audio/webm' });
        resolve(blob);
      };
      
      // 設置更頻繁的數據可用事件
      recorder.start(100); // 每100ms觸發一次ondataavailable事件
      audio.currentTime = startTime;
      audio.play();
      
      setTimeout(() => {
        audio.pause();
        recorder.stop();
      }, (endTime - startTime) * 1000);
    });
  };
  
  // Blob 轉 Base64
  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg w-full max-w-2xl p-6 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-teal-300">自定義白噪音編輯器</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="mb-4">
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setIsYoutubeMode(false)}
              className={`px-3 py-1 rounded-md ${!isYoutubeMode ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              上傳音訊檔案
            </button>
            <button
              onClick={() => setIsYoutubeMode(true)}
              className={`px-3 py-1 rounded-md ${isYoutubeMode ? 'bg-teal-600 text-white' : 'bg-gray-700 text-gray-300'}`}
            >
              YouTube 連結
            </button>
          </div>
          
          {!isYoutubeMode ? (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                音訊檔案 (MP3, MP4, M4A)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp3,.mp4,.m4a,audio/mp3,audio/mpeg,audio/mp4,audio/x-m4a,video/mp4"
                onChange={handleFileChange}
                className="w-full p-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:border-teal-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal-600 file:text-white hover:file:bg-teal-700"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                YouTube 連結
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={youtubeUrl}
                  onChange={handleYoutubeUrlChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="flex-grow p-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:border-teal-500"
                />
                <button
                  onClick={extractYoutubeAudio}
                  disabled={!youtubeUrl || isProcessing}
                  className={`px-4 py-2 rounded-md ${
                    !youtubeUrl || isProcessing
                      ? 'bg-gray-600 cursor-not-allowed'
                      : 'bg-teal-600 hover:bg-teal-700'
                  }`}
                >
                  提取
                </button>
              </div>
            </div>
          )}
        </div>
        
        {audioUrl && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              預覽與編輯
            </label>
            <audio ref={audioRef} src={audioUrl} controls className="w-full mb-2" />
            
            <div className="grid grid-cols-2 gap-4 mb-2">
              <div>
                <label className="block text-xs text-gray-400">開始時間 (秒)</label>
                <input
                  type="number"
                  min="0"
                  max={audioDuration}
                  step="0.1"
                  value={startTime}
                  onChange={handleStartTimeChange}
                  className="w-full p-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400">結束時間 (秒)</label>
                <input
                  type="number"
                  min={startTime}
                  max={audioDuration}
                  step="0.1"
                  value={endTime}
                  onChange={handleEndTimeChange}
                  className="w-full p-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            
            <input
              type="range"
              min="0"
              max={audioDuration}
              step="0.1"
              value={startTime}
              onChange={handleStartTimeChange}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer mb-2"
            />
            
            <input
              type="range"
              min="0"
              max={audioDuration}
              step="0.1"
              value={endTime}
              onChange={handleEndTimeChange}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer mb-2"
            />
            
            <button
              onClick={handlePlaySegment}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded-md text-white text-sm transition-colors"
            >
              播放選取區段
            </button>
          </div>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-300 mb-1">
            名稱
          </label>
          <input
            type="text"
            value={audioName}
            onChange={(e) => setAudioName(e.target.value)}
            placeholder="輸入音訊名稱"
            className="w-full p-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:border-teal-500"
          />
        </div>
        
        {error && (
          <div className="text-red-500 text-sm mb-4">{error}</div>
        )}
        
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-white transition-colors"
          >
            取消
          </button>
          {audioUrl && (
            <button
              onClick={downloadTrimmedAudio}
              disabled={isProcessing}
              className={`px-4 py-2 rounded-md text-white transition-colors ${
                isProcessing
                  ? 'bg-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              <span className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                下載
              </span>
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isProcessing || !audioUrl}
            className={`px-4 py-2 rounded-md text-white transition-colors ${
              isProcessing || !audioUrl
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-teal-600 hover:bg-teal-700'
            }`}
          >
            {isProcessing ? '處理中...' : '儲存'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioEditorModal;